// ** React Imports
import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

// ** MUI Imports
import Box from "@mui/material/Box";
import ChevronLeft from "mdi-material-ui/ChevronLeft";
import ChevronRight from "mdi-material-ui/ChevronRight";
import ArrowLeft from "mdi-material-ui/ArrowLeft";
import Play from "mdi-material-ui/Play";
import WeatherNight from "mdi-material-ui/WeatherNight";
import WhiteBalanceSunny from "mdi-material-ui/WhiteBalanceSunny";

// ** Components (Flix) + reused hooks/utils
import FlixLessonStage from "src/flix/components/FlixLessonStage";
import FlixPlaylist from "src/flix/components/FlixPlaylist";
import UserDropdown from "src/layouts/components/UserDropdown";
import { useAuth } from "src/hooks/useAuth";
import { useSettings } from "src/@core/hooks/useSettings";
import { pathWithPreservedSearch } from "src/@core/utils/urlQueryPreservation";

/******************************************************************
 * ##: FlixPlayerLayout
 * Self-contained Netflix-style shell for the Flix player. Resolves the
 * active chapter/lesson and builds the current-time middleware exactly
 * like the app's CoursePlayerLayout, then renders a new shell + the new
 * FlixPlaylist around the reused lesson engines. Does NOT reuse the
 * app's player chrome (header/sidebar).
 * @param {Object} props - the useFlixCoursePlayer contract
 * @returns {JSX.Element} - the `.flix-player`
 * History:
 * 22-07-2026: Created
 * 23-07-2026: "Up next" card on video end (own countdown, dismiss on cancel/seek); inline CTA near the end
 ******************************************************************/
const UP_NEXT_COUNTDOWN = 10; // the up-next card's OWN countdown (seconds), independent of the video
const CTA_NEAR_END_SECONDS = 30; // reveal the inline "Marcar como concluída" button this many seconds before the end

const FlixPlayerLayout = (props) => {
  const {
    data,
    videos_info,
    actualChapter,
    actualLesson,
    lessonsStatus,
    isFirstLesson,
    isLastLesson,
    moveToNextLesson,
    moveToPreviousLesson,
    updateVideoCurrentTime,
    updateAudioCurrentTime,
    lessonStatusHandler,
    activeLessonHandler,
    refreshLessonsStatus,
  } = props;

  const auth = useAuth();
  const location = useLocation();
  const { settings, saveSettings } = useSettings();
  const isDark = settings?.mode === "dark";

  const { course, details } = data;
  const isSmallDevice = window.innerWidth < 1400;

  // Resolve the active chapter/lesson objects (mirrors CoursePlayerLayout).
  let activeChapter = details?.find((detail) => detail.chapter_id === actualChapter);
  const activeChapterIndex = details?.findIndex((detail) => detail.chapter_id === actualChapter);
  if (activeChapter === undefined) activeChapter = details[0];

  let activeLesson = details?.find((detail) => detail.chapter_id === actualChapter)?.lessons?.find((lesson) => lesson.lesson_id === actualLesson);
  if (activeLesson === undefined) {
    activeLesson = activeChapter?.lessons[0];
    activeLessonHandler(activeChapter.chapter_id, activeLesson.lesson_id);
  }

  const isLastChapter = details[details.length - 1]?.chapter_id === actualChapter;

  const updateVideoCurrentTimeHandlerMiddleware = (video_id, currentTime) => {
    updateVideoCurrentTime(actualChapter, actualLesson, video_id, currentTime);
  };
  const updateAudioCurrentTimeHandlerMiddleware = (lesson_id, currentTime) => {
    updateAudioCurrentTime(actualLesson, lesson_id, currentTime);
  };
  const updateLessonAsCompleted = () => {
    const isQuestionnaire = activeLesson?.lesson_type === "questionnaire";
    lessonStatusHandler(actualChapter, actualLesson, true, isQuestionnaire === true ? { fromQuestionnaireSubmit: true } : {});
  };

  // --- Netflix-style "up next" + end-of-video CTA ------------------------------
  // When a video finishes, an "up next" card appears with its OWN fixed countdown
  // (independent of the video, so it works for any length — 5s or 5min) and auto-advances
  // to the next lesson (marking the current one complete) at 0. Seeking back / replaying
  // the video, or the card's Cancelar button, dismisses it — like Netflix. Separately,
  // the reused inline "Marcar aula como concluída" CTA is revealed only in the video's
  // last seconds (completeCtaVisible) instead of from the start.
  const [upNext, setUpNext] = useState({ active: false, secs: 0 });
  const [ctaNearEnd, setCtaNearEnd] = useState(false);
  const advancedRef = useRef(false);
  const advanceRef = useRef(() => {});
  advanceRef.current = () => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    updateLessonAsCompleted(); // mark the current lesson complete
    if (!isLastLesson) moveToNextLesson();
  };
  const cancelUpNext = () => setUpNext((prev) => (prev.active ? { active: false, secs: 0 } : prev));

  // Reset when the lesson changes (fresh video -> fresh state, allow advancing again).
  useEffect(() => {
    advancedRef.current = false;
    setUpNext({ active: false, secs: 0 });
    setCtaNearEnd(false);
  }, [actualChapter, actualLesson]);

  // The card's OWN countdown: tick down every second and advance at 0, independent of the
  // video (so pausing/stalling the video never affects it).
  useEffect(() => {
    if (!upNext.active) return undefined;
    if (upNext.secs <= 0) {
      advanceRef.current();
      return undefined;
    }
    const id = setTimeout(() => {
      setUpNext((prev) => (prev.active ? { ...prev, secs: prev.secs - 1 } : prev));
    }, 1000);
    return () => clearTimeout(id);
  }, [upNext.active, upNext.secs]);

  // The lesson immediately after the current one (across chapters), for the card label.
  const nextLesson = useMemo(() => {
    if (!Array.isArray(details)) return null;
    let found = false;
    for (const chapter of details) {
      for (const lesson of chapter.lessons || []) {
        if (found) return lesson;
        if (chapter.chapter_id === actualChapter && lesson.lesson_id === actualLesson) found = true;
      }
    }
    return null;
  }, [details, actualChapter, actualLesson]);

  // Real-time video progress (fires for video lessons via the reused engine).
  const handleVideoProgress = (currentTime, duration, ended) => {
    if (activeLesson?.lesson_type !== "video") return;
    if (!duration) return;
    const remaining = ended === true ? 0 : duration - currentTime;

    // Reveal the inline "mark complete" CTA only in the video's last seconds.
    setCtaNearEnd(remaining <= CTA_NEAR_END_SECONDS);

    if (ended === true) {
      if (isLastLesson) {
        advanceRef.current(); // last lesson: just mark it complete, nothing to advance to
        return;
      }
      // Video finished → show the up-next card with its own countdown.
      setUpNext((prev) => (prev.active ? prev : { active: true, secs: UP_NEXT_COUNTDOWN }));
      return;
    }
    // Video is playing well before the end (user seeked back / replayed) → dismiss any
    // pending up-next so we don't yank them out of the video they're watching.
    if (remaining > 2) {
      setUpNext((prev) => (prev.active ? { active: false, secs: 0 } : prev));
    }
  };

  // Inline CTA: always shown for non-video lessons; for videos, only in the last seconds.
  const completeCtaVisible = activeLesson?.lesson_type !== "video" || ctaNearEnd;

  const toggleMode = () => {
    saveSettings({ ...settings, mode: isDark ? "light" : "dark" });
  };

  return (
    <Box className="flix flix-player" sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "#141414" : "background.default"), color: "text.primary", minHeight: "100vh" }}>
      <header className="flix-player__topbar">
        <Link className="flix-icon-btn" to={pathWithPreservedSearch("/flix", location.search)} aria-label="Voltar ao catálogo">
          <ArrowLeft />
        </Link>
        <span className="flix-player__title">{course?.title}</span>
        <div className="flix-topbar__right">
          <button type="button" className="flix-icon-btn" onClick={toggleMode} aria-label="Alternar tema">
            {isDark ? <WhiteBalanceSunny /> : <WeatherNight />}
          </button>
          <UserDropdown settings={settings} />
        </div>
      </header>

      <div className={`flix-player__body${isSmallDevice ? " flix-player__body--stacked" : ""}`}>
        <div className="flix-player__main">
          <FlixLessonStage
            course={course}
            videos_info={videos_info}
            activeChapter={activeChapter}
            activeLesson={activeLesson}
            activeChapterIndex={activeChapterIndex}
            lessonsStatus={lessonsStatus}
            isSmallDevice={isSmallDevice}
            isLastChapter={isLastChapter}
            isLastLesson={isLastLesson}
            courseAddedAt={course?.added_at}
            courseEndDate={course?.ended_at}
            authData={auth?.user}
            auth={auth}
            updateVideoCurrentTimeHandler={updateVideoCurrentTimeHandlerMiddleware}
            updateAudioCurrentTimeHandler={updateAudioCurrentTimeHandlerMiddleware}
            onVideoProgress={handleVideoProgress}
            completeCtaVisible={completeCtaVisible}
            updateLessonAsCompleted={updateLessonAsCompleted}
            refreshLessonsStatus={refreshLessonsStatus}
            moveToNextLesson={moveToNextLesson}
          />
        </div>

        <aside className="flix-player__aside">
          <FlixPlaylist
            details={details}
            actualChapter={actualChapter}
            actualLesson={actualLesson}
            lessonsStatus={lessonsStatus}
            videos_info={videos_info}
            courseAddedAt={course?.added_at}
            courseEndDate={course?.ended_at}
            hasTeaserAccess={course?.teaser_access === true}
            showLessonNumbers={course?.show_lesson_numbers}
            showChapterNumbers={course?.show_chapter_numbers}
            activeLessonHandler={activeLessonHandler}
            lessonStatusHandler={lessonStatusHandler}
          />
        </aside>
      </div>

      {/* Prev/next lesson: fixed bottom bar on mobile only (desktop uses the
          sidebar playlist), so the controls never scroll out of reach. */}
      {isSmallDevice && (
        <nav className="flix-player__nav" aria-label="Navegação de aulas">
          <button type="button" className="flix-btn flix-btn--ghost" onClick={moveToPreviousLesson} disabled={isFirstLesson}>
            <ChevronLeft className="flix-btn__icon" />
            Aula anterior
          </button>
          <button type="button" className="flix-btn flix-btn--ghost" onClick={moveToNextLesson} disabled={isLastLesson}>
            Próxima aula
            <ChevronRight className="flix-btn__icon" />
          </button>
        </nav>
      )}

      {upNext.active && nextLesson && (
        <div className="flix-upnext" role="region" aria-label="Próxima aula">
          <span className="flix-upnext__eyebrow">A seguir</span>
          <span className="flix-upnext__title">{nextLesson.title}</span>
          <div className="flix-upnext__bar" aria-hidden="true">
            <span style={{ width: `${((UP_NEXT_COUNTDOWN - upNext.secs) / UP_NEXT_COUNTDOWN) * 100}%` }} />
          </div>
          <div className="flix-upnext__actions">
            <button type="button" className="flix-btn flix-btn--primary" onClick={() => advanceRef.current()}>
              <Play className="flix-btn__icon" />
              Próxima · {upNext.secs}s
            </button>
            <button type="button" className="flix-btn flix-btn--ghost" onClick={cancelUpNext}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </Box>
  );
};

export default FlixPlayerLayout;
